import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LandingNavbar from "@/components/LandingNavbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Mail, MapPin, Building2, FileText, Hash } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { getReadableErrorMessage } from "@/utils/errorHandler";

function ContactPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    category: "",
    message: "",
    kvkkAccepted: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.kvkkAccepted) {
      toast.error(t('contact.kvkk_required'));
      return;
    }

    setLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/contact/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || t('contact.message_sent'));
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          category: "",
          message: "",
          kvkkAccepted: false
        });
      } else {
        toast.error(getReadableErrorMessage({ response: { status: response.status, data } }, t, 'contact.message_failed'));
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(t('contact.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <LandingNavbar />
      
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#FFDD59] mb-4">{t('contact.title')}</h1>
          <p className="text-gray-400">
            {t('contact.subtitle')}
          </p>
        </div>

        {/* Company Information */}
        <Card className="bg-gray-900 border-2 border-[#FFDD59] mb-8">
          <CardHeader>
            <CardTitle className="text-[#FFDD59]">{t('contact.company_info')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-[#FFDD59] flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('contact.legal_name')}</p>
                <p className="text-white">PERSONA ORGANİZASYON TİCARET LİMİTED ŞİRKETİ</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#FFDD59] flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('contact.address_label')}</p>
                <p className="text-white">Zekeriyaköy Mah. Erguvanlı Cad. Ariköy Sitesi 28 No: 29 Sarıyer İstanbul</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-[#FFDD59] flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('contact.phone_label')}</p>
                <a href="tel:+905315043469" className="text-white hover:text-[#FFDD59] transition-colors">
                  +90 531 504 34 69
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-[#FFDD59] flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('auth.login.email_label')}</p>
                <a href="mailto:destek@opara.co" className="text-white hover:text-[#FFDD59] transition-colors">
                  destek@opara.co
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-[#FFDD59] flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('contact.tax_label')}</p>
                <p className="text-white">SARIYER / 7280821743</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Hash className="w-5 h-5 text-[#FFDD59] flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-gray-400 mb-1">{t('contact.mersis_label')}</p>
                <p className="text-white">728082174300001</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Form */}
        <Card className="bg-gray-900 border-2 border-[#FFDD59]">
          <CardHeader>
            <CardTitle className="text-[#FFDD59]">{t('contact.form_title')}</CardTitle>
            <p className="text-sm text-gray-400 mt-2">
              {t('contact.form_subtitle')}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Sol Sütun - Form Alanları (1/3 genişlik) */}
                <div className="md:col-span-1 space-y-4">
                  {/* Ad Soyad */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('contact.fullname_label')} <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={t('contact.fullname_label')}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  {/* E-posta */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('auth.login.email_label')} <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="ornek@sirket.com"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('contact.phone_label')} <span className="text-gray-500">{t('contact.optional')}</span>
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+90 5XX XXX XX XX"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  {/* Konu/Kategori */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('contact.category_label')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2"
                    >
                      <option value="">{t('contact.select_prompt')}</option>
                      <option value="sales">{t('contact.cat_sales')}</option>
                      <option value="support">{t('contact.cat_support')}</option>
                      <option value="finance">{t('contact.cat_finance')}</option>
                      <option value="general">{t('contact.cat_general')}</option>
                      <option value="other">{t('contact.cat_other')}</option>
                    </select>
                  </div>
                </div>

                {/* Sağ Sütun - Mesaj, KVKK ve Buton (2/3 genişlik) */}
                <div className="md:col-span-2 space-y-4">
                  {/* Mesaj */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('contact.message_label')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder={t('contact.message_placeholder')}
                      rows="7"
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2"
                    />
                  </div>

                  {/* KVKK Onayı */}
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="kvkk"
                      checked={formData.kvkkAccepted}
                      onChange={(e) => setFormData({...formData, kvkkAccepted: e.target.checked})}
                      className="mt-1"
                    />
                    <label htmlFor="kvkk" className="text-sm text-gray-400">
                      {t('contact.terms_consent').split(/(\{TERMS\}|\{KVKK\})/).map((seg, i) => {
                        if (seg === '{TERMS}') return <a key={i} href="/kullanim-kosullari" target="_blank" className="text-[#FFDD59] hover:underline">{t('contact.terms_link')}</a>;
                        if (seg === '{KVKK}') return <a key={i} href="/kvkk" target="_blank" className="text-[#FFDD59] hover:underline">{t('contact.kvkk_link')}</a>;
                        return seg;
                      })} <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold py-3"
                  >
                    {loading ? t('contact.sending') : t('contact.send_btn')}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}

export default ContactPage;
